import axios, { AxiosError, AxiosResponse } from "axios";
import { call, put, takeLatest } from "@redux-saga/core/effects";
import storage from "redux-persist/lib/storage";

import { persistReducer } from "redux-persist";
import { RootState } from "data-handler/rootReducer";

export const FEATURE_FLAGS_REQUEST = "schoolconnect/featureflags/REQUEST";
export const FEATURE_FLAGS_SUCCESS = "schoolconnect/featureflags/SUCCESS";
export const FEATURE_FLAGS_FAILURE = "schoolconnect/featureflags/FAILURE";

export type FeatureFlags = {
  [key: string]: boolean;
};

type State = {
  featureFlagsFetching: boolean;
  featureFlagsError: any;
  featureFlagsData: FeatureFlags;
};

type FeatureFlagsRequestAction = {
  type: typeof FEATURE_FLAGS_REQUEST;
};

type FeatureFlagsSuccessAction = {
  type: typeof FEATURE_FLAGS_SUCCESS;
  data: FeatureFlags;
};

type FeatureFlagsFailureAction = {
  type: typeof FEATURE_FLAGS_FAILURE;
  error: AxiosError;
};

type Action =
  | FeatureFlagsRequestAction
  | FeatureFlagsSuccessAction
  | FeatureFlagsFailureAction;

const EMPTY_FEATURE_FLAGS_DATA: FeatureFlags = {};

const initialState: State = {
  featureFlagsFetching: false,
  featureFlagsError: null,
  featureFlagsData: EMPTY_FEATURE_FLAGS_DATA,
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case FEATURE_FLAGS_REQUEST:
      return {
        ...state,
        featureFlagsFetching: true,
        featureFlagsError: null,
      };
    case FEATURE_FLAGS_SUCCESS:
      return {
        ...state,
        featureFlagsFetching: false,
        featureFlagsError: null,
        featureFlagsData: action.data,
      };
    case FEATURE_FLAGS_FAILURE:
      return {
        ...state,
        featureFlagsFetching: false,
        featureFlagsError: action.error,
      };
    default:
      return state;
  }
};

const persistedReducer = persistReducer(
  {
    key: "featureFlags",
    storage,
    blacklist: ["featureFlagsFetching", "featureFlagsError"],
  },
  reducer
);

export default persistedReducer;

function fetchFeatureFlags() {
  const url = `${process.env.REACT_APP_API_URL}/feature-flags/`;
  return axios({
    method: "GET",
    url: url,
  });
}

export function* workerFeatureFlagsSaga(action: FeatureFlagsRequestAction) {
  try {
    const response: AxiosResponse = yield call(fetchFeatureFlags);
    const data = response.data;
    yield put({ type: FEATURE_FLAGS_SUCCESS, data });
  } catch (error) {
    yield put({ type: FEATURE_FLAGS_FAILURE, error });
  }
}

export function* featureFlagsSagaWatcher() {
  yield takeLatest(FEATURE_FLAGS_REQUEST, workerFeatureFlagsSaga);
}

export const requestFeatureFlags = () => ({
  type: FEATURE_FLAGS_REQUEST,
});

export const getFeatureFlags = (state: RootState): FeatureFlags =>
  state.featureFlags?.featureFlagsData || EMPTY_FEATURE_FLAGS_DATA;

export const getFeatureFlag = (flagKey: string) => (
  state: RootState
): boolean => getFeatureFlags(state)[flagKey] || false;

export const isFeatureFlagEnabled = (
  flagKey: string,
  state: RootState
): boolean => getFeatureFlag(flagKey)(state);

export const getIsFeatureFlagsFetching = (state: RootState): boolean =>
  state.featureFlags.featureFlagsFetching;

export const getFeatureFlagsError = (state: RootState): AxiosError =>
  state.featureFlags.featureFlagsError;
